// Используем относительный путь для продакшена, или полный URL для разработки
const API_BASE_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:8000/api');

// Получить токен из localStorage
export const getToken = (): string | null => {
  return localStorage.getItem('auth_token');
};

// Сохранить токен в localStorage
export const setToken = (token: string): void => {
  localStorage.setItem('auth_token', token);
};

// Удалить токен из localStorage
export const removeToken = (): void => {
  localStorage.removeItem('auth_token');
};

const LS_EXTERNAL_ACCESS = 'external_access_token';
const LS_EXTERNAL_REFRESH = 'external_refresh_token';
const LS_EXTERNAL_REFRESH_EXP = 'external_refresh_expires_at';

export const setExternalSession = (
  access: string,
  refresh: string,
  refreshExpiresAt?: string | null
): void => {
  localStorage.setItem(LS_EXTERNAL_ACCESS, access);
  localStorage.setItem(LS_EXTERNAL_REFRESH, refresh);
  if (refreshExpiresAt) {
    localStorage.setItem(LS_EXTERNAL_REFRESH_EXP, refreshExpiresAt);
  }
};

export const removeExternalSession = (): void => {
  localStorage.removeItem(LS_EXTERNAL_ACCESS);
  localStorage.removeItem(LS_EXTERNAL_REFRESH);
  localStorage.removeItem(LS_EXTERNAL_REFRESH_EXP);
};

export const getExternalAccessToken = (): string | null => localStorage.getItem(LS_EXTERNAL_ACCESS);

export const getExternalRefreshToken = (): string | null => localStorage.getItem(LS_EXTERNAL_REFRESH);

/**
 * Синхронизация external access JWT:
 * — Фронт: перед apiRequest дергаем refresh-external при необходимости → обновляется localStorage.
 * — Laravel: bearerForExternalOutbound() минтит JWT для camback (заголовок с фронта не нужен).
 * Запас до exp access (мс). По умолчанию 120_000 = за 2 мин до истечения уже обновляем, чтобы в сети не уходил «мёртвый» JWT. 0 = только после exp. Env: REACT_APP_EXTERNAL_ACCESS_REFRESH_MARGIN_MS
 */
const EXTERNAL_ACCESS_REFRESH_MARGIN_MS = (() => {
  const n = Number(process.env.REACT_APP_EXTERNAL_ACCESS_REFRESH_MARGIN_MS);
  return Number.isFinite(n) && n >= 0 ? n : 120_000;
})();

function getJwtExpMs(jwt: string): number | null {
  try {
    const parts = jwt.split('.');
    if (parts.length !== 3) return null;
    let b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const pad = b64.length % 4;
    if (pad) b64 += '='.repeat(4 - pad);
    const payload = JSON.parse(atob(b64)) as { exp?: number };
    return typeof payload.exp === 'number' ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

function needsExternalAccessRefresh(): boolean {
  const access = getExternalAccessToken();
  if (!access) {
    return false;
  }
  const expMs = getJwtExpMs(access);
  if (expMs == null) {
    return true;
  }
  const msLeft = expMs - Date.now();
  if (EXTERNAL_ACCESS_REFRESH_MARGIN_MS === 0) {
    return msLeft <= 0;
  }
  return msLeft < EXTERNAL_ACCESS_REFRESH_MARGIN_MS;
}

function needsExternalRefreshWindowSoon(): boolean {
  const expStr = localStorage.getItem(LS_EXTERNAL_REFRESH_EXP);
  if (!expStr) {
    return false;
  }
  const expMs = new Date(expStr).getTime();
  if (Number.isNaN(expMs)) {
    return false;
  }
  return expMs - Date.now() <= 2 * 24 * 60 * 60 * 1000;
}

let externalRefreshInFlight: Promise<void> | null = null;

/** Восстановить external JWT по Sanctum, если refresh в LS устарел (ротация, миграция). */
async function bootstrapExternalSession(): Promise<boolean> {
  const token = getToken();
  if (!token) {
    return false;
  }

  const res = await fetch(`${API_BASE_URL}/auth/bootstrap-external`, {
    method: 'POST',
    credentials: 'omit',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });

  const responseText = await res.text();
  let data: Record<string, unknown> = {};
  try {
    data =
      responseText && responseText.trim()
        ? (JSON.parse(responseText) as Record<string, unknown>)
        : {};
  } catch {
    data = {};
  }

  if (!res.ok) {
    return false;
  }

  const access = data.external_access_token as string | undefined;
  const nextRefresh = data.external_refresh_token as string | undefined;
  const exp = data.external_refresh_expires_at as string | undefined;
  if (access && nextRefresh) {
    setExternalSession(access, nextRefresh, exp ?? null);
    return true;
  }

  return false;
}

async function refreshExternalSession(): Promise<void> {
  const refresh = getExternalRefreshToken();
  if (!refresh) {
    throw new Error('Нет refresh-токена внешней сессии');
  }

  const res = await fetch(`${API_BASE_URL}/auth/refresh-external`, {
    method: 'POST',
    credentials: 'omit',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({ refresh_token: refresh }),
  });

  const responseText = await res.text();
  let data: Record<string, unknown> = {};
  try {
    data =
      responseText && responseText.trim()
        ? (JSON.parse(responseText) as Record<string, unknown>)
        : {};
  } catch {
    data = {};
  }

  if (!res.ok) {
    if (res.status === 401) {
      removeExternalSession();
      if (getToken() && (await bootstrapExternalSession())) {
        return;
      }
    }
    throw new Error((data.message as string) || 'Ошибка обновления внешней сессии');
  }

  const access = data.external_access_token as string | undefined;
  const nextRefresh = data.external_refresh_token as string | undefined;
  const exp = data.external_refresh_expires_at as string | undefined;
  if (access && nextRefresh) {
    setExternalSession(access, nextRefresh, exp ?? null);
  }
}

/** Продлевает внешнюю сессию: за ~2 суток до конца refresh ИЛИ access-JWT в зоне запаса до exp / истёк → новая пара в LS на следующих запросах. */
async function maybeRefreshExternalSession(): Promise<void> {
  if (!getExternalRefreshToken()) {
    return;
  }
  // Достаточно либо Sanctum (основной сценарий), либо уже сохранённого external access для проверки срока
  if (!getToken() && !getExternalAccessToken()) {
    return;
  }

  const accessSoon = needsExternalAccessRefresh();
  const refreshSoon = needsExternalRefreshWindowSoon();

  if (!accessSoon && !refreshSoon) {
    return;
  }

  if (externalRefreshInFlight) {
    await externalRefreshInFlight;
    return;
  }

  externalRefreshInFlight = (async () => {
    try {
      await refreshExternalSession();
    } catch (e) {
      console.warn('maybeRefreshExternalSession:', e);
    } finally {
      externalRefreshInFlight = null;
    }
  })();

  await externalRefreshInFlight;
}

/**
 * Перед прямым HTTP во внешнее API (минуя Laravel) — при необходимости обновить пару external access/refresh
 * (тот же срок жизни access, что на бэкенде: по умолчанию 30 мин).
 */
export async function ensureExternalAccessFresh(): Promise<void> {
  await maybeRefreshExternalSession();
}

/**
 * Добавляет external access JWT в query (?token=…) для GET к camback (скриншоты /api/screens/…).
 */
export function appendExternalAccessTokenQuery(
  resourceUrl: string,
  token?: string | null
): string {
  const extJwt = token ?? getExternalAccessToken();
  if (!extJwt) {
    return resourceUrl;
  }

  try {
    const u = new URL(resourceUrl);
    u.searchParams.set('token', extJwt);
    return u.toString();
  } catch {
    const sep = resourceUrl.includes('?') ? '&' : '?';
    return `${resourceUrl}${sep}token=${encodeURIComponent(extJwt)}`;
  }
}

/**
 * URL фото с ?token= для <img src={...}>.
 */
export async function fetchAuthenticatedExternalBlobUrl(resourceUrl: string): Promise<string> {
  await ensureExternalAccessFresh();
  const extJwt = getExternalAccessToken();
  if (!extJwt) {
    throw new Error('Нет external access JWT. Войдите в систему или обновите внешнюю сессию.');
  }

  return appendExternalAccessTokenQuery(resourceUrl, extJwt);
}

/** Не показывать пользователю HTML от nginx (502/504) и длинные простыни */
function humanizeHttpErrorMessage(raw: string, status: number): string {
  const s = (raw || '').trim();
  const looksLikeHtml = /<html[\s>]/i.test(s) || /<!DOCTYPE/i.test(s);
  if (looksLikeHtml || /502 Bad Gateway/i.test(s) || /504 Gateway Time-?out/i.test(s) || /503 Service Unavailable/i.test(s)) {
    if (status === 502 || /502 Bad Gateway/i.test(s)) {
      return 'Сервер временно недоступен (502 Bad Gateway). Проверьте PHP-FPM и nginx на хосте.';
    }
    if (status === 504 || /504 Gateway/i.test(s)) {
      return 'Шлюз не дождался ответа (504). Попробуйте позже или увеличьте таймауты.';
    }
    if (status === 503 || /503 Service Unavailable/i.test(s)) {
      return 'Сервис недоступен (503). Попробуйте позже.';
    }
    return 'Сервер вернул страницу ошибки вместо JSON. Проверьте бэкенд и прокси.';
  }
  return raw;
}

// Базовый запрос с авторизацией
export const apiRequest = async <T>(
  endpoint: string,
  options: RequestInit = {},
  isRetry = false
): Promise<T> => {
  const token = getToken();
  if (token) {
    await maybeRefreshExternalSession();
  }

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    ...(options.headers as Record<string, string>),
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers,
    credentials: 'omit',
  });

  // Получаем текст ответа для проверки
  const responseText = await response.text();
  
  // Проверяем, что ответ не пустой
  if (!responseText || responseText.trim() === '') {
    throw new Error(`Пустой ответ от сервера. Статус: ${response.status} ${response.statusText}`);
  }

  // Пытаемся распарсить JSON
  let data;
  try {
    data = JSON.parse(responseText);
  } catch (e) {
    // Если не JSON, значит сервер вернул HTML или другой формат
    console.error('Ошибка парсинга JSON:', e);
    console.error('Ответ сервера:', responseText.substring(0, 500));
    
    if (responseText.includes('<html') || responseText.includes('<!DOCTYPE')) {
      throw new Error(humanizeHttpErrorMessage(responseText, response.status));
    }
    
    throw new Error(`Сервер вернул невалидный JSON. Ответ: ${responseText.substring(0, 200)}`);
  }

  if (!response.ok) {
    // Формируем сообщение: приоритет у data.error (уже короткий текст от бэкенда, например detail от внешнего API)
    let errorMessage = data.message || 'Ошибка при выполнении запроса';

    if (data.error) {
      const errorDetail = typeof data.error === 'string' ? data.error : JSON.stringify(data.error);
      errorMessage = errorDetail || errorMessage;
    } else if (data.details && typeof data.details === 'object') {
      const detailsStr = JSON.stringify(data.details);
      if (detailsStr !== '{}') {
        errorMessage += ` (${detailsStr})`;
      }
    }
    
    // Добавляем ошибки валидации, если они есть
    if (data.errors) {
      const validationErrors = Object.values(data.errors).flat().join(', ');
      errorMessage += ` ${validationErrors}`;
    }

    errorMessage = humanizeHttpErrorMessage(errorMessage, response.status);

    if (
      !isRetry &&
      response.status === 401 &&
      data?.code === 'EXTERNAL_SESSION_EXPIRED' &&
      getToken()
    ) {
      removeExternalSession();
      if (await bootstrapExternalSession()) {
        return apiRequest<T>(endpoint, options, true);
      }
      removeToken();
    }

    const error = new Error(errorMessage);
    (error as any).response = data;
    (error as any).status = response.status;
    throw error;
  }

  return data;
};

type AuthTokensResponse = {
  success: boolean;
  user: { id: number; name: string; email: string; phone?: string | null };
  token: string;
  external_access_token?: string;
  external_refresh_token?: string;
  external_refresh_expires_at?: string | null;
};

// API методы для авторизации
export const authAPI = {
  login: async (email: string, password: string) => {
    const response = await apiRequest<AuthTokensResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });

    if (response.success && response.token) {
      setToken(response.token);
    }
    if (
      response.success &&
      response.external_access_token &&
      response.external_refresh_token
    ) {
      setExternalSession(
        response.external_access_token,
        response.external_refresh_token,
        response.external_refresh_expires_at ?? null
      );
    }

    return response;
  },

  register: async (name: string, email: string, password: string, password_confirmation: string) => {
    const response = await apiRequest<AuthTokensResponse>('/auth/register', {
      method: 'POST',
      body: JSON.stringify({ name, email, password, password_confirmation }),
    });

    if (response.success && response.token) {
      setToken(response.token);
    }
    if (
      response.success &&
      response.external_access_token &&
      response.external_refresh_token
    ) {
      setExternalSession(
        response.external_access_token,
        response.external_refresh_token,
        response.external_refresh_expires_at ?? null
      );
    }

    return response;
  },

  logout: async () => {
    try {
      await apiRequest('/auth/logout', {
        method: 'POST',
      });
    } finally {
      removeToken();
      removeExternalSession();
    }
  },

  getUser: async () => {
    return apiRequest<{
      success: boolean;
      user: { id: number; name: string; email: string; phone?: string | null };
    }>('/auth/user');
  },

  updateProfile: async (data: { name?: string; email?: string; phone?: string | null }) => {
    return apiRequest<{
      success: boolean;
      message: string;
      user: { id: number; name: string; email: string; phone?: string | null };
    }>('/auth/profile', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  changePassword: async (data: { old_password: string; password: string; password_confirmation: string }) => {
    return apiRequest<{
      success: boolean;
      message: string;
    }>('/auth/change-password', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
};

// API методы для участков
export const areasAPI = {
  getAll: async () => {
    return apiRequest<{
      success: boolean;
      areas: Array<{ id: number; name: string; description: string | null; cameras_count?: number }>;
    }>('/areas');
  },

  getOne: async (id: number) => {
    return apiRequest<{
      success: boolean;
      area: { id: number; name: string; description: string | null };
    }>(`/areas/${id}`);
  },

  create: async (name: string, description?: string) => {
    return apiRequest<{
      success: boolean;
      message: string;
      area: { id: number; name: string; description: string | null };
    }>('/areas', {
      method: 'POST',
      body: JSON.stringify({ name, description }),
    });
  },

  update: async (id: number, name: string, description?: string) => {
    return apiRequest<{
      success: boolean;
      message: string;
      area: { id: number; name: string; description: string | null };
    }>(`/areas/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ name, description }),
    });
  },

  delete: async (id: number) => {
    return apiRequest<{
      success: boolean;
      message: string;
    }>(`/areas/${id}`, {
      method: 'DELETE',
    });
  },
};

// API методы для моделей (аналитик)
export const modelsAPI = {
  getAll: async () => {
    // Ходим на внутренний Laravel-бэкенд, который сам проксирует запрос
    // во внешний API (см. backend/app/Http/Controllers/ModelController.php)
    return apiRequest<{
      success: boolean;
      models: string[];
    }>('/models');
  },
};

// API методы для камер
export const camerasAPI = {
  getAll: async () => {
    return apiRequest<{
      success: boolean;
      cameras: Array<{
        id: number;
        name: string;
        rtsp_uri: string;
        area_id: number;
        models: string[];
        camera_id: string | null;
      }>;
    }>('/cameras');
  },

  getByArea: async (areaId: number) => {
    console.log('Fetching cameras for area:', areaId);
    const response = await apiRequest<{
      success: boolean;
      cameras: Array<{
        id: number | null;
        name: string;
        rtsp_uri: string;
        area_id: number;
        models: string[];
        camera_id: string | null;
        hls_out?: string | null;
        webrtc_out?: string | null;
        status?: any;
      }>;
    }>(`/areas/${areaId}/cameras`);
    console.log('Cameras API response:', response);
    return response;
  },

  getOne: async (id: number) => {
    return apiRequest<{
      success: boolean;
      camera: {
        id: number;
        name: string;
        rtsp_uri: string;
        area_id: number;
        models: string[];
        camera_id: string | null;
      };
    }>(`/cameras/${id}`);
  },

  create: async (name: string, rtspUri: string, areaId: number, models: string[] = []) => {
    return apiRequest<{
      success: boolean;
      message: string;
      camera: {
        id: number;
        name: string;
        rtsp_uri: string;
        area_id: number;
        models: string[];
        camera_id: string | null;
      };
    }>('/cameras', {
      method: 'POST',
      body: JSON.stringify({ name, rtsp_uri: rtspUri, area_id: areaId, models }),
    });
  },

  update: async (id: number, name?: string, rtspUri?: string, models?: string[]) => {
    const data: any = {};
    if (name) data.name = name;
    if (rtspUri) data.rtsp_uri = rtspUri;
    if (models) data.models = models;

    return apiRequest<{
      success: boolean;
      message: string;
      camera: {
        id: number;
        name: string;
        rtsp_uri: string;
        area_id: number;
        models: string[];
        camera_id: string | null;
      };
    }>(`/cameras/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  /** Обновление камеры во внешнем API по camera_id (имя, RTSP и др.). */
  patchBySourceId: async (
    cameraId: string,
    data: { name?: string; rtsp_uri?: string; name_en?: string; reserve_name?: string }
  ) => {
    const sid = encodeURIComponent(cameraId);
    return apiRequest<{
      success: boolean;
      message?: string;
      camera?: {
        id: number;
        name: string;
        rtsp_uri: string;
        area_id: number;
        models: string[];
        camera_id: string | null;
      };
    }>(`/cameras/${sid}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  delete: async (id: number) => {
    return apiRequest<{
      success: boolean;
      message: string;
    }>(`/cameras/${id}`, {
      method: 'DELETE',
    });
  },

  // ROI методы - отправка через наш бэкенд
  setRoi: async (
    cameraId: string,
    model: string,
    roiType: 'pre_crop' | 'post_filter',
    coords: { x: number; y: number; w: number; h: number } | null
  ) => {
    const data: any = { model };
    
    if (roiType === 'pre_crop') {
      data.pre_crop = coords;
      data.post_filter = [];
    } else {
      data.pre_crop = null;
      data.post_filter = coords ? [coords] : [];
    }

    return apiRequest<{
      success: boolean;
      message: string;
      data?: any;
    }>(`/cameras/${encodeURIComponent(cameraId)}/roi`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  // Порог уверенности
  setConfidence: async (cameraId: string, model: string, minConfidence: number) => {
    return apiRequest<{
      success: boolean;
      message: string;
      data?: any;
    }>(`/cameras/${encodeURIComponent(cameraId)}/confidence`, {
      method: 'POST',
      body: JSON.stringify({ model, min_confidence: minConfidence }),
    });
  },

  // Кулдаун между событиями
  setCooldown: async (cameraId: string, model: string, cooldown: number) => {
    return apiRequest<{
      success: boolean;
      message: string;
      data?: any;
    }>(`/cameras/${encodeURIComponent(cameraId)}/cooldown`, {
      method: 'POST',
      body: JSON.stringify({ model, cooldown }),
    });
  },

  /** Привязать к камере ещё одну модель детекции (POST внешнему API через бэкенд). */
  attachModel: async (cameraId: string, modelName: string) => {
    const sid = encodeURIComponent(cameraId);
    return apiRequest<{
      success: boolean;
      message?: string;
      camera?: unknown;
    }>(`/cameras/${sid}/models`, {
      method: 'POST',
      body: JSON.stringify({ name: modelName }),
    });
  },

  enablePeopleCounter: async (cameraId: string) => {
    return apiRequest<{
      success: boolean;
      message?: string;
      data?: unknown;
    }>(`/cameras/${encodeURIComponent(cameraId)}/people-counter/enable`, {
      method: 'POST',
    });
  },

  disablePeopleCounter: async (cameraId: string) => {
    return apiRequest<{
      success: boolean;
      message?: string;
      data?: unknown;
    }>(`/cameras/${encodeURIComponent(cameraId)}/people-counter/disable`, {
      method: 'POST',
    });
  },

  getPeopleCounterStatus: async (cameraId: string) => {
    return apiRequest<{
      success: boolean;
      enabled: boolean;
      data?: unknown;
    }>(`/cameras/${encodeURIComponent(cameraId)}/people-counter/status`);
  },

  /** Отвязать одну модель от камеры, остальные не трогая. */
  detachModel: async (cameraId: string, modelName: string) => {
    const sid = encodeURIComponent(cameraId);
    const mn = encodeURIComponent(modelName);
    return apiRequest<{
      success: boolean;
      message?: string;
      camera?: unknown;
    }>(`/cameras/${sid}/models/${mn}`, {
      method: 'DELETE',
    });
  },
};

// API методы для логов
export const logsAPI = {
  getByArea: async (
    areaId: number,
    page: number = 1,
    perPage: number = 20,
    options?: {
      /**
       * Включать ли тяжёлую синхронизацию с внешним API на бэке.
       * По умолчанию бэк делает sync на page=1, поэтому для "Показать ещё"/фонового обновления
       * важно явно передавать sync=0.
       */
      sync?: boolean;
      /** Запрашивать ли json_data (может быть тяжёлым). */
      includeJson?: boolean;
      /**
       * Ограничение для синка с внешним API на бэке (см. ext_max в LogController).
       * Полезно для "лёгкого" фонового обновления.
       */
      extMax?: number;
      /** Фильтр логов по камере участка (id в БД). Без параметра — все камеры. */
      cameraId?: number;
      /** Фильтр логов по модели (точное совпадение с `logs.model`). */
      model?: string;
      /** Границы периода по `detection_timestamp`, формат YYYY-MM-DD (включительно по дням в TZ приложения). */
      dateFrom?: string;
      dateTo?: string;
    }
  ) => {
    const params = new URLSearchParams();
    params.set('page', String(page));
    params.set('per_page', String(perPage));
    if (options?.sync !== undefined) params.set('sync', options.sync ? '1' : '0');
    if (options?.includeJson !== undefined) params.set('include_json', options.includeJson ? '1' : '0');
    if (options?.extMax !== undefined) params.set('ext_max', String(options.extMax));
    if (options?.cameraId !== undefined && Number.isFinite(options.cameraId)) {
      params.set('camera_id', String(options.cameraId));
    }
    if (options?.model) {
      params.set('model', String(options.model));
    }
    if (options?.dateFrom && /^\d{4}-\d{2}-\d{2}$/.test(options.dateFrom)) {
      params.set('date_from', options.dateFrom);
    }
    if (options?.dateTo && /^\d{4}-\d{2}-\d{2}$/.test(options.dateTo)) {
      params.set('date_to', options.dateTo);
    }

    return apiRequest<{
      success: boolean;
      logs: Array<{
        timestamp: string;
        model: string;
        screen_url: string | null;
        json_data: any;
      }>;
      total: number;
      page: number;
      per_page: number;
      total_pages: number;
    }>(`/areas/${areaId}/logs?${params.toString()}`);
  },

  getModelsByArea: async (
    areaId: number,
    options?: {
      cameraId?: number;
      dateFrom?: string;
      dateTo?: string;
    }
  ) => {
    const params = new URLSearchParams();
    if (options?.cameraId !== undefined && Number.isFinite(options.cameraId)) {
      params.set('camera_id', String(options.cameraId));
    }
    if (options?.dateFrom && /^\d{4}-\d{2}-\d{2}$/.test(options.dateFrom)) {
      params.set('date_from', options.dateFrom);
    }
    if (options?.dateTo && /^\d{4}-\d{2}-\d{2}$/.test(options.dateTo)) {
      params.set('date_to', options.dateTo);
    }
    const qs = params.toString();

    return apiRequest<{
      success: boolean;
      models: string[];
    }>(`/areas/${areaId}/logs/models${qs ? `?${qs}` : ''}`);
  },

  getStatsByArea: async (
    areaId: number,
    options?: {
      cameraId?: number;
    }
  ) => {
    const params = new URLSearchParams();
    if (options?.cameraId !== undefined && Number.isFinite(options.cameraId)) {
      params.set('camera_id', String(options.cameraId));
    }
    const qs = params.toString();

    return apiRequest<{
      success: boolean;
      day: {
        total: number;
        from?: string;
        to?: string;
        by_model: Array<{ model: string; count: number }>;
      };
      week: {
        total: number;
        from?: string;
        to?: string;
        by_model: Array<{ model: string; count: number }>;
        by_day: Array<{ date: string; label: string; count: number }>;
      };
    }>(`/areas/${areaId}/logs/stats${qs ? `?${qs}` : ''}`);
  },
};

// Экспорт для использования в других частях приложения
export default apiRequest;
